import { Check } from "@mui/icons-material";
import { Button, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { NetworkRequestState } from "../../../../common/models";
import { UpdateCollectionGroup } from "../../../../common/service/server/controller/CollectionGroupsController";
import { CollectionGroupDetailsDto } from "../../../../common/service/server/dto/CollectionGroupDto";
import { usePrimaryOrganizationId } from "../../../hooks";
import { useCollectionGroups, useUpdateCollectionGroups } from "../../../hooks/useCollectionGroups";
import { DataMapDto } from "../../../../common/service/server/dto/datamap/DataMapDto";
import { useDataMap } from "../../../../common/hooks/api/useDataMap";
import { useActionRequest } from "../../../../common/hooks/api";
import { useAllCollectedPICByCollectionGroup } from "../../../../common/hooks/dataMapHooks";
import { Api } from "../../../../common/service/Api";

export const DATA_RETENTION_SURVEY_NAME = "data-retention-survey";

export type UseHRDataRetentionModule = {
  requests: {
    dataMapRequest: NetworkRequestState;
    collectionGroupsRequest: NetworkRequestState;
  };
  isPublished: boolean;
  isFinished: boolean;
  updateRequest: NetworkRequestState;
  publishRequest: NetworkRequestState;
  props: HRDataRetentionModuleProps;
};

export const useHRDataRetentionModule = (): UseHRDataRetentionModule => {
  const orgId = usePrimaryOrganizationId();

  const { data: dataMap, request: dataMapRequest } = useDataMap(orgId);
  const [collectionGroups, collectionGroupsRequest] = useCollectionGroups(orgId, ["EMPLOYMENT"]);

  const { updateCollectionGroup, updateCollectionGroupRequest } = useUpdateCollectionGroups(orgId);
  const filteredCollectionGroups = collectionGroups.filter((cg) =>
    dataMap.collection.some((col) => col.groupId === cg.id),
  );

  const {
    fetch: publishEmployeeGroupHrDataRetentionPolicies,
    request: publishEmployeeGroupHrDataRetentionPoliciesRequest,
  } = useActionRequest({
    api: async () => {
      await Api.consumerGroups.publishEmployeeGroupHrDataRetentionPolicies(orgId);
    },
  });

  const [onLoad, setOnLoad] = useState(false);
  const [approvedGroups, setApprovedGroups] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (dataMap?.collection?.length && collectionGroups?.length) {
      setOnLoad(true);
    }
  }, [dataMap, collectionGroups]);
  useEffect(() => {
    if (onLoad) {
      setApprovedGroups((old) => {
        filteredCollectionGroups?.forEach((dto) => {
          if (dto.hrDataRetentionPolicyDraft && dto.hrDataRetentionPolicyApproved) {
            old.add(dto.id);
          }
        });
        return new Set(old);
      });
    }
  }, [onLoad]);

  const isPublished = filteredCollectionGroups.every(
    (g) =>
      g.hrDataRetentionPolicyApproved &&
      g.hrDataRetentionPolicy &&
      g.hrDataRetentionPolicy.length > 0,
  );

  const isFinished = filteredCollectionGroups.every(
    (g) =>
      approvedGroups.has(g.id) && g.hrDataRetentionPolicyDraft && g.hrDataRetentionPolicyApproved,
  );

  return {
    requests: {
      dataMapRequest,
      collectionGroupsRequest,
    },
    isPublished,
    isFinished,
    updateRequest: updateCollectionGroupRequest,
    publishRequest: publishEmployeeGroupHrDataRetentionPoliciesRequest,
    props: {
      employeeGroups: filteredCollectionGroups,
      map: dataMap,
      updateCollectionGroup,
      publishEmployeeGroupHrDataRetentionPolicies,
      setApprovedGroups: (id: string, approved: boolean) => {
        setApprovedGroups((old) => {
          if (approved) {
            old.add(id);
          } else {
            old.delete(id);
          }
          return new Set(old);
        });
      },
    },
  };
};

export type HRDataRetentionModuleProps = {
  employeeGroups: CollectionGroupDetailsDto[];
  map: DataMapDto;
  updateCollectionGroup: (args: {
    groupId: string;
    params: UpdateCollectionGroup;
  }) => Promise<void>;
  publishEmployeeGroupHrDataRetentionPolicies: () => Promise<void>;
  setApprovedGroups: (id: string, approved: boolean) => void;
};
export const HRDataRetentionModule: React.FC<HRDataRetentionModuleProps> = ({
  employeeGroups,
  map,
  updateCollectionGroup,
  setApprovedGroups,
}) => {
  const handleGroupUpdate = (
    id,
    args: { hrDataRetentionPolicyDraft: string; hrDataRetentionPolicyApproved: boolean },
  ) => {
    updateCollectionGroup({
      groupId: id,
      params: {
        hrDataRetentionPolicyDraft: args.hrDataRetentionPolicyDraft,
        hrDataRetentionPolicyApproved: args.hrDataRetentionPolicyApproved,
      },
    });
  };

  return (
    <div style={{ marginTop: "60px" }}>
      {employeeGroups.map((g) => {
        return (
          <HRGroupDataRetentionQuestion
            group={g}
            key={g.id}
            map={map}
            onUpdate={(args) => {
              handleGroupUpdate(g.id, args);
            }}
            setGroupApproved={(approved) => {
              setApprovedGroups(g.id, approved);
            }}
          />
        );
      })}
    </div>
  );
};

type HRGroupDataRetentionQuestionProps = {
  group: CollectionGroupDetailsDto;
  map: DataMapDto;
  onUpdate: (arg: {
    hrDataRetentionPolicyDraft: string;
    hrDataRetentionPolicyApproved: boolean;
  }) => void;
  setGroupApproved?: (approved: boolean) => void;
};
const HRGroupDataRetentionQuestion: React.FC<HRGroupDataRetentionQuestionProps> = ({
  group,
  map,
  onUpdate,
  setGroupApproved,
}) => {
  const collected = useAllCollectedPICByCollectionGroup(map, group);

  const [approved, setApproved] = useState(
    group.hrDataRetentionPolicyDraft && group.hrDataRetentionPolicyApproved,
  );
  const [description, setDescription] = useState(group.hrDataRetentionPolicyDraft ?? "");

  const handleApproved = () => {
    setApproved(true);
    setGroupApproved(true);
    onUpdate({
      hrDataRetentionPolicyDraft: description,
      hrDataRetentionPolicyApproved: true,
    });
  };

  if (!collected || collected.length === 0) {
    return null;
  }

  const isEmpty = typeof description !== "string" || description.trim().length === 0;

  return (
    <div className="mb-xxl px-md py-md border-color-neutral-300">
      <h6 className="my-0 text-weight-medium">{group.name}</h6>

      <div className="mx-md">
        <h6 className="mb-xs text-weight-medium d-flex flex-row flex--align-center">
          <img src="/assets/images/surveys/onboarding/data-collection-icon.svg" className="mr-md" />
          Data you Collect
        </h6>
        <p className="my-0 ml-xl">{collected.map((c) => c.name).join(", ")}</p>
      </div>

      <div className="pt-lg">
        <h6 className="text-weight-medium mt-0 ml-md mb-md">Custom Retention Period</h6>
        <div className="pr-md mb-md">
          <TextField
            className="freetext--textfield ml-md"
            variant="outlined"
            value={description}
            fullWidth={true}
            onChange={(e) => {
              setDescription(e.target.value);
              setApproved(false);
              setGroupApproved(false);
            }}
            multiline={true}
            rows={2}
            placeholder="Enter your criteria for retention"
          />
        </div>
        <div className="text-component-help mb-md flex-grow ml-md">
          Example: We retain employment information for a minimum of 4 years from the date of
          termination of an employee/contractor or non-hire of an applicant, and possibly longer if
          legally obligated.
        </div>
        <div className="d-flex flex-row flex--align-center justify-end">
          <Button
            variant="contained"
            color="primary"
            disabled={approved || isEmpty}
            className="min-width-128"
            onClick={handleApproved}
            startIcon={approved ? <Check /> : undefined}
          >
            {approved ? "Saved" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
};
